/**
 * ExportManager Module
 * Handles image generation and export functionality
 */
import type { RenderConfig, ExportFormat, ImageData } from '../../types';
import type { ImageRenderer } from './imageRenderer';
import type { Analytics } from '../../services/analytics';
import { downloadDataUrl } from '../../utils/downloadHelper';

export class ExportManager {
    imageRenderer: ImageRenderer;
    analytics: Analytics;
    isGenerating: boolean;

    constructor(imageRenderer: ImageRenderer, analytics: Analytics) {
        this.imageRenderer = imageRenderer;
        this.analytics = analytics;
        this.isGenerating = false;
    }

    /**
     * Generate and download image
     * @param config - Generation configuration
     */
    async generateAndDownload(config: RenderConfig): Promise<void> {
        if (this.isGenerating) {
            return;
        }
        
        const startTime = Date.now();
        this.isGenerating = true;
        
        const button = document.getElementById('generateBtn') as HTMLButtonElement | null;
        const originalText = button ? button.innerHTML : '';
        
        try {
            // Show loading state
            if (button) {
                this.showLoadingState(button, 'Generating...');
            }
            
            // Small delay to show loading state
            await this.delay(100);
            
            // Generate image
            this.imageRenderer.renderImage(config);
            
            // Small delay to ensure canvas is ready
            await this.delay(500);
            
            // Export and download
            await this.exportImage(config.format || 'png');
            
            // Track generation
            const imageData: ImageData = {
                width: config.width,
                height: config.height,
                format: config.format || 'png',
                hasText: config.text ? config.text.trim() !== '' : false,
                hasBackgroundImage: config.backgroundImage !== null,
                generationTime: Date.now() - startTime
            };
            this.analytics.trackImageGeneratedAndDownloaded(imageData);
            
        } catch (error) {
            this.analytics.trackExportError((error as Error).message);
            alert('Failed to generate image. Please try again.');
        } finally {
            // Reset button state
            if (button) {
                this.resetButton(button, originalText);
            }
            this.isGenerating = false;
        }
    }

    /**
     * Export image as file download
     * @param format - Image format
     */
    async exportImage(format: ExportFormat = 'png'): Promise<void> {
        try {
            const canvas = this.imageRenderer.canvas;
            if (!canvas) {
                throw new Error('Canvas not available');
            }
            
            const mimeType = this.getMimeType(format);
            const dataURL = canvas.toDataURL(mimeType, 0.9);
            const filename = `image-${canvas.width}x${canvas.height}.${format}`;
            
            // Show thank you modal and download immediately
            if (window.appModal) {
                window.appModal.showThankYou();
            }
            
            // Use cross-browser compatible download helper
            downloadDataUrl(dataURL, filename);
            
            // Track export
            const exportData: ImageData = {
                width: canvas.width,
                height: canvas.height,
                format: format,
                hasText: false,
                hasBackgroundImage: false,
                fileSize: Math.round(dataURL.length * 0.75).toString() // Approximate file size
            };
            this.analytics.trackImageExported(exportData);
            
        } catch (error) {
            this.analytics.trackExportError((error as Error).message);
            throw error;
        }
    }

    /**
     * Get MIME type for format
     * @param format - Format name
     * @returns MIME type
     */
    getMimeType(format: string): string {
        const mimeTypes: Record<string, string> = {
            'jpeg': 'image/jpeg',
            'jpg': 'image/jpeg',
            'webp': 'image/webp',
            'png': 'image/png',
            'bmp': 'image/bmp'
        };
        return mimeTypes[format.toLowerCase()] || 'image/png';
    }

    /**
     * Show loading state on button
     * @param button - Button element
     * @param text - Loading text
     */
    showLoadingState(button: HTMLButtonElement, text: string = 'Loading...'): void {
        button.innerHTML = `
            <div class="button-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-dasharray="32" stroke-dashoffset="32">
                        <animateTransform attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" values="0 10 10;360 10 10"/>
                    </circle>
                </svg>
            </div>
            <span>${text}</span>
        `;
        button.disabled = true;
    }

    /**
     * Reset button to normal state
     * @param button - Button element
     * @param originalText - Original button HTML
     */
    resetButton(button: HTMLButtonElement, originalText: string): void {
        button.innerHTML = originalText;
        button.disabled = false;
    }

    /**
     * Utility delay function
     * @param ms - Milliseconds to delay
     * @returns Promise that resolves after delay
     */
    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Check if currently generating
     * @returns True if generating
     */
    isCurrentlyGenerating(): boolean {
        return this.isGenerating;
    }

    /**
     * Export canvas as blob (for advanced use cases)
     * @param format - Image format
     * @param quality - Image quality (0-1)
     * @returns Promise resolving to image blob
     */
    async exportAsBlob(format: ExportFormat = 'png', quality: number = 0.9): Promise<Blob | null> {
        return this.imageRenderer.exportAsBlob(format, quality);
    }

    /**
     * Export canvas as data URL (for advanced use cases)
     * @param format - Image format
     * @param quality - Image quality (0-1)
     * @returns Data URL
     */
    exportAsDataURL(format: ExportFormat = 'png', quality: number = 0.9): string {
        return this.imageRenderer.exportAsDataURL(format, quality);
    }
}
